import { useId, type ComponentPropsWithRef, type ReactNode } from "react";
import { Select, type ComboboxData } from "@mantine/core";

import { cn } from "@/lib/cn";

const CONTROL_CLASS = cn(
  "w-full rounded-md border border-border-subtle bg-surface px-3.5 py-2.5 text-sm text-ink",
  "placeholder:text-ink-muted/70 disabled:bg-surface-muted disabled:text-ink-muted",
  "aria-[invalid=true]:border-danger",
  "focus-visible:border-brand focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand",
);

interface FieldShellProps {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

/**
 * Wires a control to its label, hint and error.
 *
 * Doing it here rather than at each call site is what makes `aria-describedby`
 * reliably present: a field whose error is only red text is invisible to
 * anyone not looking at the colour.
 */
function FieldShell({ id, label, hint, error, required, children }: FieldShellProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex flex-col gap-2">
      {/* The asterisk sits outside the <label> on purpose: inside, it would
          become part of the accessible name ("DevEUI*"). The `required`
          attribute on the control is what actually conveys this. */}
      <div className="flex items-center gap-0.5">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
        </label>
        {required ? (
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </div>

      {children({ id, describedBy })}

      {hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * `ComponentPropsWithRef` rather than `InputHTMLAttributes`: React Hook Form
 * registers a field by handing back a `ref`, and in React 19 that arrives as
 * an ordinary prop which has to reach the real `<input>`.
 */
interface TextFieldProps extends Omit<ComponentPropsWithRef<"input">, "id"> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({
  label,
  hint,
  error,
  className,
  // Business forms must not surface the browser's prior-entry list. Login
  // opts back in with `autoComplete="username"` / `current-password`.
  autoComplete = "off",
  ...rest
}: TextFieldProps) {
  const generatedId = useId();

  return (
    <FieldShell
      id={generatedId}
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(rest.required === undefined ? {} : { required: rest.required })}
    >
      {({ id, describedBy }) => (
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(CONTROL_CLASS, className)}
          {...rest}
          autoComplete={autoComplete}
        />
      )}
    </FieldShell>
  );
}

interface TextAreaFieldProps extends Omit<ComponentPropsWithRef<"textarea">, "id"> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextAreaField({
  label,
  hint,
  error,
  className,
  autoComplete = "off",
  ...rest
}: TextAreaFieldProps) {
  const generatedId = useId();

  return (
    <FieldShell
      id={generatedId}
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(rest.required === undefined ? {} : { required: rest.required })}
    >
      {({ id, describedBy }) => (
        <textarea
          id={id}
          rows={3}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(CONTROL_CLASS, "resize-y", className)}
          {...rest}
          autoComplete={autoComplete}
        />
      )}
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps {
  label: string;
  data: ComboboxData;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  name?: string;
  onBlur?: () => void;
  className?: string;
}

/**
 * Searchable select backed by Mantine (ADR-075).
 *
 * Empty string remains the sentinel for "All / Unassigned / Choose…". Search
 * turns on automatically once there are more than five options; short enums
 * stay a plain list. Call sites can override with `searchable`.
 */
export function SelectField({
  label,
  data,
  value,
  onChange,
  hint,
  error,
  required,
  disabled,
  searchable,
  name,
  onBlur,
  className,
}: SelectFieldProps) {
  const generatedId = useId();
  const optionCount = countSelectOptions(data);
  const enableSearch = searchable ?? optionCount > 5;

  return (
    <FieldShell
      id={generatedId}
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      {...(required === undefined ? {} : { required })}
    >
      {({ id, describedBy }) => (
        <Select
          id={id}
          data={data}
          value={value}
          onChange={(next) => onChange(next ?? "")}
          searchable={enableSearch}
          nothingFoundMessage="No matches"
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn("w-full", className)}
          allowDeselect={false}
          // Keep the dropdown inside the parent (including Modals). A portal
          // lands outside the dialog and Mantine marks it aria-hidden, which
          // hides every option from assistive tech and from the test queries.
          comboboxProps={{ withinPortal: false }}
          {...(name === undefined ? {} : { name })}
          {...(onBlur === undefined ? {} : { onBlur })}
          {...(disabled === undefined ? {} : { disabled })}
          {...(required === undefined ? {} : { required })}
        />
      )}
    </FieldShell>
  );
}

function countSelectOptions(data: ComboboxData): number {
  let count = 0;

  for (const item of data) {
    if (typeof item === "string") {
      count += 1;
      continue;
    }

    if ("group" in item) {
      count += item.items.length;
      continue;
    }

    count += 1;
  }

  return count;
}
